/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **One pill** (`LP9`, `docs/LISTS_AND_PANELS_DESIGN.md`).
 *
 * Pills are how a row, a title bar or a collapsed section says what an item **is**, and they carry the
 * most meaning when there are few kinds and each looks the same everywhere. So there is one height
 * (20px), one radius (the design system's 6px badge radius) and one rule:
 *
 * > **Colour means state or severity, and nothing else.** Green, amber and red appear only where a
 * > thing is healthy, needs attention, or is wrong. Categories — a protocol, a type, a tag — are
 * > neutral, because a protocol is not good or bad.
 *
 * Three consequences are built in here rather than left to each caller:
 *
 * - **A pill that is a control shows it** — a button role, a focus ring, a hover ring and a chevron —
 *   and **a pill that is not** renders as plain text with no affordance. `LP8`: the pill is the control
 *   for the state it shows, so there is no separate Enable button beside it.
 * - **Icons are Lucide, never emoji.** An emoji renders differently per platform and reads as
 *   decoration; an icon is part of the vocabulary.
 * - **Evidence is not drawn here.** It has its own vocabulary (`BADGE_STATES`, `SourceValidationBadge`),
 *   which must never be derived from configuration; a second way to draw it is how that would drift.
 */

import React from 'react';
import { Box, chakra } from '@chakra-ui/react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

export type PillTone = 'neutral' | 'success' | 'warning' | 'error' | 'info';

/**
 * The tone a pill takes. **`neutral` is the default and the answer for every category**; the others are
 * for a state that is healthy (`success`), needs attention (`warning`) or is wrong (`error`). `info`
 * is for a state that is neither — a draft, a template — never for a category.
 */
const TONES: Record<PillTone, { bg: string; color: string; border: string }> = {
  neutral: { bg: 'bg.subtle', color: 'fg.muted', border: 'border.default' },
  success: { bg: 'success.tint', color: 'success.ink', border: 'success.border' },
  warning: { bg: 'warning.tint', color: 'warning.ink', border: 'warning.border' },
  error: { bg: 'error.tint', color: 'error.ink', border: 'error.border' },
  info: { bg: 'info.tint', color: 'info.ink', border: 'info.border' },
};

export interface PillProps {
  tone: PillTone;
  /** A Lucide icon drawn before the label. */
  icon?: LucideIcon;
  /** A 6px dot before the label, in the tone's ink — a state pill's marker. */
  dot?: boolean;
  /** Italic, for a value that names a role rather than a person (*System*). */
  italic?: boolean;
  /**
   * **Makes the pill a control.** Absent, it is a statement: plain, unfocusable and with no hover.
   * The click does not reach a row the pill sits in.
   */
  onClick?: () => void;
  disabled?: boolean;
  /**
   * **Let the click bubble**, for a pill that is the trigger of something wrapped around it — the
   * owner's transfer list opens from a popover trigger outside the pill. The wrapper is then what
   * keeps the click from reaching a row.
   */
  propagate?: boolean;
  /** The control's accessible name, where the label alone does not say what pressing it does. */
  ariaLabel?: string;
  /** Set by `ControlHover`, which binds its sentence to the control it is about (`#1276`). */
  'aria-describedby'?: string;
  children: React.ReactNode;
}

const PillButton = chakra('button');
const PillSpan = chakra('span');

export const Pill = React.forwardRef<HTMLElement, PillProps>(function Pill(
  { tone, icon: Icon, dot, italic, onClick, disabled, propagate, ariaLabel, 'aria-describedby': describedBy, children },
  ref,
) {
  const t = TONES[tone];
  const inner = (
    <>
      {dot && <Box as="span" display="inline-block" w="6px" h="6px" borderRadius="full" bg={t.color} flexShrink={0} />}
      {Icon && <Icon size={12} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0 }} />}
      <Box as="span" fontStyle={italic ? 'italic' : undefined}>
        {children}
      </Box>
      {onClick && <ChevronDown size={12} strokeWidth={2} aria-hidden="true" style={{ flexShrink: 0, opacity: 0.7 }} />}
    </>
  );
  const common = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    h: '20px',
    px: 2,
    borderRadius: 'md',
    borderWidth: '1px',
    borderColor: t.border,
    bg: t.bg,
    color: t.color,
    fontSize: 'xs',
    fontWeight: 600,
    lineHeight: '18px',
    whiteSpace: 'nowrap' as const,
    'data-pill-tone': tone,
    'aria-describedby': describedBy,
  };
  if (!onClick) {
    return (
      <PillSpan ref={ref as React.Ref<HTMLSpanElement>} {...common}>
        {inner}
      </PillSpan>
    );
  }
  return (
    <PillButton
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      {...common}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.6 : undefined}
      disabled={disabled}
      aria-label={ariaLabel}
      _hover={disabled ? undefined : { borderColor: 'accent.solid' }}
      _focusVisible={{ outline: 'none', boxShadow: 'focusRing', borderColor: 'accent.solid' }}
      onClick={(e: React.MouseEvent) => {
        // A pill in a row is its own control (`LP8`): pressing it must not also open the row.
        if (!propagate) e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        // Enter on the pill is the pill's, not the row's.
        if (!propagate && (e.key === 'Enter' || e.key === ' ')) e.stopPropagation();
      }}
    >
      {inner}
    </PillButton>
  );
});

/**
 * A **category** (`LP9`'s *tag* kind) — a protocol, an optimizer type, a role. Always neutral, always
 * a statement: a category is not a verdict, so it borrows no state colour (`TYPE_BADGE_BG` drew VAST
 * in the accent and API in success green, so a protocol pill read as good or bad).
 */
export const TagPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Pill tone="neutral">{children}</Pill>
);
